import React, { useState, useEffect } from 'react';
import { DEVICES } from '../../constants/breakpoints';
import './Style.css';

const CreateClassName = ({
  selectedElement,
  updateElementClassName,
  elements,
  setElements,
  globalClassNames,
  addToGlobalClassNames,
  classNames,
  setClassNames,
  classStylesRegistry,
  setClassStylesRegistry,
  combinedClassStylesRegistry,
  setCombinedClassStylesRegistry,
  setPages,
  currentPageId,
  selectedDevice,
  setSelectedElement,
  getDefaultStyles
}) => {
  const [showDropdown, setShowDropdown] = useState(null);
  const [searchValue, setSearchValue] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);

  useEffect(() => {
    // console.log("selectedElement IN CREATE CLASS NAME", selectedElement);
  }, [selectedElement]);

  const handleClassNameDelete = (index) => {
    const updatedClassNames = classNames.filter((_, i) => i !== index);
    setClassNames(updatedClassNames);
    updateElementClassName(selectedElement.id, updatedClassNames);
  };

  const handleAddClass = async (value) => {
    if (value && !classNames.includes(value)) {
      // Check if class already exists in registry by cascading through breakpoints
      const getExistingStyles = (className, device) => {
        let styles = {};
        
        switch (device) {
          case DEVICES.MOBILE:
            styles = {
              ...classStylesRegistry[DEVICES.DESKTOP]?.get(className) || {},
              ...classStylesRegistry[DEVICES.TABLET]?.get(className) || {},
              ...classStylesRegistry[DEVICES.MOBILE]?.get(className) || {}
            };
            break;
          case DEVICES.TABLET:
            styles = {
              ...classStylesRegistry[DEVICES.DESKTOP]?.get(className) || {},
              ...classStylesRegistry[DEVICES.TABLET]?.get(className) || {}
            };
            break;
          default:
            styles = classStylesRegistry[device]?.get(className) || {};
        }
        
        return styles;
      };

      // Check if class exists in any breakpoint
      const existingStyles = getExistingStyles(value, selectedDevice);
      const isExistingClass = Object.keys(existingStyles).length > 0;

      // If it's a new class, get default styles and initialize registries
      if (!isExistingClass) {
        const defaultStyles = getDefaultStyles(selectedElement.type.toLowerCase(),selectedElement);
        
        // Add to global class names
        addToGlobalClassNames(value);

        // Initialize class in registry with default styles
        setClassStylesRegistry(prev => {
          const newRegistry = { ...prev };
          const deviceMap = new Map(prev[selectedDevice]);
          deviceMap.set(value, defaultStyles);
          newRegistry[selectedDevice] = deviceMap;
          return newRegistry;
        });

        // Update page's class registry
        setPages(prevPages => 
          prevPages.map(page => {
            if (page.id === currentPageId) {
              const updatedClassStylesRegistry = { ...page.classStylesRegistry };
              const deviceRegistry = new Map(updatedClassStylesRegistry[selectedDevice]);
              deviceRegistry.set(value, defaultStyles);
              updatedClassStylesRegistry[selectedDevice] = deviceRegistry;

              return {
                ...page,
                classStylesRegistry: updatedClassStylesRegistry
              };
            }
            return page;
          })
        );
      }

      // If this is a second class, update combinedClassStylesRegistry
      if (classNames.length > 0) {
        const combinationKey = [...classNames, value].join('+');

        // Check if combination exists by cascading through breakpoints
        const getExistingCombinedStyles = (key, device) => {
          let styles = {};
          
          switch (device) {
            case DEVICES.MOBILE:
              styles = {
                ...combinedClassStylesRegistry[DEVICES.DESKTOP]?.get(key) || {},
                ...combinedClassStylesRegistry[DEVICES.TABLET]?.get(key) || {},
                ...combinedClassStylesRegistry[DEVICES.MOBILE]?.get(key) || {}
              };
              break;
            case DEVICES.TABLET:
              styles = {
                ...combinedClassStylesRegistry[DEVICES.DESKTOP]?.get(key) || {},
                ...combinedClassStylesRegistry[DEVICES.TABLET]?.get(key) || {}
              };
              break;
            default:
              styles = combinedClassStylesRegistry[device]?.get(key) || {};
          }
          
          return styles;
        };

        const existingCombinedStyles = getExistingCombinedStyles(combinationKey, selectedDevice);
        const isCombinationExists = Object.keys(existingCombinedStyles).length > 0;

        if (!isCombinationExists) {
          let baseStyles = {};
          
          if (classNames.length > 1) {
            const existingCombinationKey = classNames.join('+');
            baseStyles = getExistingCombinedStyles(existingCombinationKey, selectedDevice);
            
            if (Object.keys(baseStyles).length === 0) {
              baseStyles = getExistingStyles(classNames[0], selectedDevice);
            }
          } else {
            baseStyles = getExistingStyles(classNames[0], selectedDevice);
          }

          // Update combinedClassStylesRegistry
          setCombinedClassStylesRegistry(prev => {
            const newRegistry = { ...prev };
            const deviceMap = new Map(prev[selectedDevice] || new Map());
            deviceMap.set(combinationKey, {
              ...baseStyles
            });
            newRegistry[selectedDevice] = deviceMap;
            return newRegistry;
          });
        }
      }

      // Update element's class names
      const updatedClassNames = [...classNames, value];
      setClassNames(updatedClassNames);
      updateElementClassName(selectedElement.id, updatedClassNames);
    }
    setSearchValue("");
    setShowSuggestions(false);
  };

  return (
    <div className="property-group">
      <div className="property-group-header">
        <span className="property-group-title">Classes</span>
      </div>
      
      <div className="class-names-container">
        {/* Show input only if there are existing classes */}
        {classNames.length > 0 && (
          <div className="class-input-wrapper">
            <input
              className="class-input modern"
              type="text"
              placeholder="Add class..."
              value={searchValue}
              onChange={(e) => {
                setSearchValue(e.target.value);
                setShowSuggestions(true);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" && searchValue) {
                  handleAddClass(searchValue);
                }
              }}
            />
            {showSuggestions && searchValue && (
              <div className="class-suggestions modern">
                {Array.from(globalClassNames)
                  .filter(
                    (className) =>
                      className.toLowerCase().includes(searchValue.toLowerCase()) &&
                      !classNames.includes(className)
                  )
                  .map((suggestion) => (
                    <button
                      key={suggestion}
                      className="suggestion-item modern"
                      onClick={() => handleAddClass(suggestion)}
                    >
                      {suggestion}
                    </button>
                  ))}
              </div>
            )}
          </div>
        )}

        {/* Class Tags */}
        <div className={`class-tags-wrapper ${classNames.length === 0 ? 'empty' : ''}`}>
          {classNames.map((className, index) => (
            <div key={index} className="class-tag modern">
              <span className="class-tag-text">
                {className}
              </span>
              {/* Only show delete button on last class and in default state */}
              {index === classNames.length - 1 && 
               selectedElement?.currentState === 'default' && (
                <button
                  className="class-tag-delete"
                  onClick={() => handleClassNameDelete(index)}
                  title="Remove class"
                >
                  ×
                </button>
              )}
            </div>
          ))}
          
          {/* Show "Add class" placeholder when no classes exist */}
          {classNames.length === 0 && (
            <input
              className="class-input modern empty-state"
              type="text"
              placeholder="Add class..."
              value={searchValue}
              onChange={(e) => {
                setSearchValue(e.target.value);
                setShowSuggestions(true);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" && searchValue) {
                  handleAddClass(searchValue);
                }
              }}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default CreateClassName; 